/**
 * CarteAction.tsx — S231 lot Z2 (ZONES.CONNECTEES.1 ; DEC-HE2T-1 Q9 b ; DEC-ZONES-GESTES-1 ZQ2 a, ZQ3 a, ZQ4 a ; DEC-ZONES-GESTES-2)
 * La carte d'action du geste en attente, sous la dernière bulle, à part des options de CarlOS ; classes recopiées de
 * docs/sources/maquette-discussion/Z-GESTES.dc.html:23-25 (sha256 db488bdfbfe30778), sans rien inventer ; icône ArrowRight de lucide-react
 * (même tracé que la planche), trait stroke-[2.5] (DEC-ZONES-GESTES-2). Texte, titre et boutons viennent du serveur (/elements
 * carte_action, greffier.carte_du_geste).
 */
import { ArrowRight } from "lucide-react";
import { EVT_RELIRE_ELEMENTS, type CarteActionServeur, type ChoixGesteServeur } from "./hooks/useDiscussionElements";
import { executerChoixGeste } from "./hooks/useGestesDiscussion";
import { EVT_COCHER_CARTES } from "./boites/riche";

/** Événement : mettre le curseur dans le champ de la discussion (ZQ3 a, « Le préciser », « Demander une modification ») */
export const EVT_FOCUS_DISCUSSION = "bt-focus-discussion";

/** Ce que fait un bouton de la carte : « route » (executerChoixGeste puis relecture de /elements), « aller_boite » (défile jusqu'à la boîte
 *  visée, ZQ2 a, ZQ4 a) ou « focus_discussion » (curseur dans le champ, ZQ3 a). */
export function actionDuBoutonCarte(b: Partial<ChoixGesteServeur> | null | undefined): "route" | "aller_boite" | "focus_discussion" | null {
  if (!b) return null;
  if (b.route) return "route";
  if (b.action_ecran === "aller_boite" || b.action_ecran === "focus_discussion") return b.action_ecran;
  return null;
}

/** P-S232-36 (G2, F1) : l'aide montrée sur les boutons de la carte pendant que Carlos écrit (boutons inactifs, comme Envoyer et les options). */
export const AIDE_CARTE_INACTIVE = "Carlos répond encore — attendez la fin de sa réponse.";

/** Le clic d'un bouton de la carte. Une route : la même que la pilule (executerChoixGeste, sans message envoyé) ; executerChoixGeste fait
 *  relire /elements par les deux zones (P-S232-36 F4 : une seule relecture, plus de second EVT_RELIRE_ELEMENTS ici). Aller à la boîte : la
 *  première boîte [data-boite-greffier=boîte visée]. Le champ : EVT_FOCUS_DISCUSSION (ChatBoxV3). */
export async function cliquerBoutonCarte(fil: string, carte: CarteActionServeur, b: ChoixGesteServeur): Promise<void> {
  const quoi = actionDuBoutonCarte(b);
  if (quoi === "route") {
    await executerChoixGeste(fil, b, b.texte);
  } else if (quoi === "aller_boite") {
    document.querySelector(`[data-boite-greffier="${carte.boite}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    // S231 lot D (DEC-ZONES-GESTES-3) : « Accepter » de « Ce qu'on sait » coche d'avance les faits du client « proposée »
    if (carte.faits?.length) window.dispatchEvent(new CustomEvent(EVT_COCHER_CARTES, { detail: { ids: carte.faits } }));
  } else if (quoi === "focus_discussion") {
    window.dispatchEvent(new CustomEvent(EVT_FOCUS_DISCUSSION));
  }
}

/** inactif (P-S232-36 F1) : pendant que Carlos écrit, les boutons sont désactivés et portent l'aide AIDE_CARTE_INACTIVE (avant : toujours
 *  cliquables, double clic et geste pendant le flux possibles). */
export function CarteAction({ carte, onBouton, inactif = false }: { carte: CarteActionServeur; onBouton: (b: ChoixGesteServeur) => void; inactif?: boolean }) {
  return (
    <div role="group" aria-label="Action attendue dans le workspace" className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      data-carte-action={carte.geste} data-carte-boite={carte.boite} data-carte-inactive={inactif ? "1" : undefined}>
      <div className="bg-[#00B4D8]/10 px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <ArrowRight className="h-4 w-4 stroke-[2.5] text-blue-600 shrink-0" aria-hidden="true" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">À faire dans le workspace</span>
        </div>
        <p className="text-sm font-bold text-gray-900">{carte.titre}</p>
        <p className="text-xs text-gray-700 mt-1 leading-relaxed">{carte.texte}</p>
      </div>
      <div className="px-4 py-3 flex flex-wrap gap-2 border-t border-gray-100">
        {carte.boutons.map((b, i) => (
          <button key={b.id} type="button" data-bouton-carte={b.id} onClick={() => onBouton(b)} disabled={inactif}
            title={inactif ? AIDE_CARTE_INACTIVE : undefined} style={inactif ? { opacity: 0.4, cursor: "default" } : undefined}
            className={i === 0 ? "text-xs bg-gray-900 text-white px-4 py-2 rounded-lg font-bold"
              : "text-xs bg-white text-gray-700 px-4 py-2 rounded-lg font-bold border border-gray-200"}>
            {b.texte}
          </button>
        ))}
      </div>
    </div>
  );
}
